/**
 * The theme's colours and fonts, read from the page's CSS variables, for drawing the graph.
 */
import { CATEGORY_COLOURS, NO_CATEGORY_VAR } from "../../shared/categories";

/** Colours and fonts read from the theme's CSS variables. */
export interface Palette {
  canvas: string;
  fg: string;
  muted: string;
  accent: string;
  inherit: string;
  depend: string;
  use: string;
  /** The category colours, `--category-1` first (a category's place in the graph's order picks one). */
  categories: string[];
  /** The colour of nodes in no category. */
  noCategory: string;
  fontUi: string;
  fontMono: string;
}

/** Reads the palette from the page's current theme. */
export function readPalette(): Palette {
  const css = getComputedStyle(document.documentElement);
  /** Reads one CSS variable. */
  const v = (name: string) => css.getPropertyValue(name).trim();
  return {
    canvas: v("--canvas"),
    fg: v("--fg"),
    muted: v("--muted"),
    accent: v("--accent"),
    inherit: v("--e-inherit"),
    depend: v("--e-depend"),
    use: v("--e-use"),
    categories: Array.from({ length: CATEGORY_COLOURS }, (_, i) => v(`--category-${i + 1}`)),
    noCategory: v(NO_CATEGORY_VAR),
    fontUi: v("--font-ui"),
    fontMono: v("--font-mono"),
  };
}

/**
 * The palette entries that read empty: the page's stylesheet was not applied yet when they
 * were read (it happens in some web views, such as WebKit in the desktop app). Drawing with an
 * empty colour silently paints in black.
 */
export function missingColours(p: Palette): string[] {
  return Object.entries(p).flatMap(([key, value]) => {
    if (Array.isArray(value)) return value.flatMap((one, i) => (one ? [] : [`${key}[${i}]`]));
    return value ? [] : [key];
  });
}
